import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Calendar, MapPin, Droplets,
  FileCheck2, AlertCircle, Lightbulb, ClipboardList,
} from "lucide-react";
import api from "@/lib/api";
import { TankCleanContext } from "@/components/TankCleanContext";
import { PartnerShell } from "./Dashboard";

const BAND_TONE = {
  excellent: { bg: "bg-emerald-100", text: "text-emerald-800", label: "Excellent" },
  good:      { bg: "bg-green-100",   text: "text-green-800",   label: "Good" },
  caution:   { bg: "bg-amber-100",   text: "text-amber-800",   label: "Caution" },
  action:    { bg: "bg-rose-100",    text: "text-rose-800",    label: "Action" },
  critical:  { bg: "bg-rose-200",    text: "text-rose-900",    label: "Critical" },
  unsafe:    { bg: "bg-rose-100",    text: "text-rose-900",    label: "Unsafe" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU"); } catch { return "—"; }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU"); } catch { return "—"; }
}

const Row = ({ label, children, testid }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 py-2 border-b border-slate-100 last:border-b-0">
    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 w-40 shrink-0">{label}</div>
    <div className="text-sm text-slate-800 break-words flex-1" data-testid={testid}>{children}</div>
  </div>
);

export default function PartnerPassportDetail() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get(`/partner/passports/${encodeURIComponent(propertyId)}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.response?.status === 404
            ? "Record not found or not accessible to your franchisee."
            : (e?.response?.data?.detail || "Unable to load passport"));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [propertyId]);

  return (
    <PartnerShell active="passports">
      <div className="mb-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="partner-passport-back-btn"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
      </div>

      {loading && (
        <div className="rounded-xl bg-white border border-slate-200 p-8 text-center text-slate-500" data-testid="partner-passport-detail-loading">
          Loading passport…
        </div>
      )}
      {error && !loading && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 p-6 text-rose-800 flex items-start gap-3" data-testid="partner-passport-detail-error">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <div className="font-semibold">{error}</div>
            <Link to="/partner/passports" className="text-sm underline mt-2 inline-block">Back to passports</Link>
          </div>
        </div>
      )}
      {data && !loading && !error && (
        <PassportDetailBody data={data} />
      )}
    </PartnerShell>
  );
}

function PassportDetailBody({ data }) {
  const p = data.property;
  const latest = data.latest;
  const tone = BAND_TONE[(latest?.band || "").toLowerCase()] || BAND_TONE.good;
  const tests = data.tests || [];
  const recs = data.recommendations || [];
  const schedules = data.schedules || [];
  const relatedBookings = data.related_bookings || [];

  return (
    <>
      {/* ── Header card with score ───────────────────────────────── */}
      <div className="rounded-xl bg-white border border-slate-200 p-6 mb-5" data-testid="partner-passport-detail">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700 mb-1">AWARE Water Health Record</div>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-900" data-testid="partner-passport-detail-address">
              {p.nickname || p.address}
            </h1>
            {p.nickname && <div className="text-sm text-slate-500 mt-1">{p.address}</div>}
            <div className="text-sm text-slate-600 mt-1 inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-slate-400" />
              <span data-testid="partner-passport-detail-owner">{p.owner_email || "—"}</span>
              <span className="text-slate-300">·</span>
              <span>{p.property_type || "—"}</span>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className={`w-20 h-20 rounded-2xl ${tone.bg} ${tone.text} flex flex-col items-center justify-center`}>
              <ShieldCheck className="w-6 h-6" />
              <div className="text-2xl font-bold mt-0.5" data-testid="partner-passport-detail-score">
                {latest?.health_score ?? "—"}
              </div>
            </div>
            <div>
              <div className={`inline-flex text-[10px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 ${tone.bg} ${tone.text}`} data-testid="partner-passport-detail-band">
                {tone.label}
              </div>
              <div className="text-xs text-slate-500 mt-1">Safety: <span className="font-semibold text-slate-700" data-testid="partner-passport-detail-safety">{data.safety_status || "—"}</span></div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* ── Key findings & latest result ─────────────────────────── */}
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Droplets className="w-4 h-4 text-cyan-700" />
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Latest test</h2>
          </div>
          {latest ? (
            <div data-testid="partner-passport-latest-test">
              <Row label="Test package" testid="partner-passport-latest-name">{latest.test_name || latest.test_id}</Row>
              <Row label="Collected" testid="partner-passport-latest-collected">{fmtDateTime(latest.collected_at)}</Row>
              <Row label="Published" testid="partner-passport-latest-published">{fmtDateTime(latest.published_at)}</Row>
              <Row label="Sample #" testid="partner-passport-latest-sample">{latest.sample_number || "—"}</Row>
              <Row label="Booking" testid="partner-passport-latest-booking">
                {latest.booking_reference ? (
                  <code className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded">{latest.booking_reference}</code>
                ) : "—"}
              </Row>
            </div>
          ) : (
            <div className="text-sm text-slate-500 italic">No published tests yet.</div>
          )}
        </div>

        {/* ── Retest schedule ──────────────────────────────────────── */}
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-cyan-700" />
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Retest schedule</h2>
          </div>
          {schedules.length === 0 ? (
            <div className="text-sm text-slate-500 italic" data-testid="partner-passport-schedules-empty">No retest scheduled.</div>
          ) : (
            <ul className="space-y-2" data-testid="partner-passport-schedules">
              {schedules.map((s, i) => (
                <li key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-b-0 last:pb-0">
                  <span className="text-slate-700">
                    {s.test_id ? <code className="text-[11px] font-mono bg-slate-100 px-1.5 py-0.5 rounded mr-2">{s.test_id}</code> : null}
                    {s.status || "scheduled"}
                  </span>
                  <span className="font-semibold text-slate-900">{fmtDate(s.next_due_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Recommendations ──────────────────────────────────────── */}
        <div className="rounded-xl bg-white border border-slate-200 p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="w-4 h-4 text-cyan-700" />
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Recommendations</h2>
          </div>
          <TankCleanContext recommended={data.tank_clean_recommended} context={data.tank_clean_context} testIdPrefix="partner-passport-tank-context" />
          {recs.length === 0 ? (
            <div className="text-sm text-slate-500 italic" data-testid="partner-passport-recs-empty">No recommendations triggered for the latest result.</div>
          ) : (
            <ul className="space-y-2.5" data-testid="partner-passport-recs">
              {recs.map((r, i) => (
                <li key={i} className="text-sm bg-slate-50 rounded-lg p-3 border border-slate-100" data-testid={`partner-passport-rec-${i}`}>
                  <div className="font-semibold text-slate-900">{r.title || r.label || `Recommendation #${i + 1}`}</div>
                  {r.body && <div className="text-slate-700 mt-1">{r.body}</div>}
                  {r.detail && <div className="text-slate-600 mt-1 text-xs">{r.detail}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Test history ─────────────────────────────────────────── */}
        <div className="rounded-xl bg-white border border-slate-200 p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <ClipboardList className="w-4 h-4 text-cyan-700" />
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Test history</h2>
            <span className="text-[10px] text-slate-500 ml-1">{tests.length} record{tests.length === 1 ? "" : "s"}</span>
          </div>
          {tests.length === 0 ? (
            <div className="text-sm text-slate-500 italic">No tests on record.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="partner-passport-history">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Test</th>
                    <th className="px-3 py-2">Score</th>
                    <th className="px-3 py-2">Band</th>
                    <th className="px-3 py-2">Booking</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tests.map((t) => {
                    const tt = BAND_TONE[(t.band || "").toLowerCase()] || BAND_TONE.good;
                    return (
                      <tr key={t.id} data-testid={`partner-passport-history-row-${t.id}`}>
                        <td className="px-3 py-2 text-slate-700">{fmtDate(t.collected_at)}</td>
                        <td className="px-3 py-2 text-slate-700">{t.test_name || t.test_id}</td>
                        <td className="px-3 py-2 font-bold text-slate-900">{t.health_score ?? "—"}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex text-[10px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 ${tt.bg} ${tt.text}`}>
                            {tt.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {t.booking_reference ? <code className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded">{t.booking_reference}</code> : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Related partner bookings ─────────────────────────────── */}
        <div className="rounded-xl bg-white border border-slate-200 p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <FileCheck2 className="w-4 h-4 text-cyan-700" />
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Related bookings</h2>
            <span className="text-[10px] text-slate-500 ml-1">attributed to your franchisee</span>
          </div>
          {relatedBookings.length === 0 ? (
            <div className="text-sm text-slate-500 italic">No bookings on record yet.</div>
          ) : (
            <ul className="space-y-2" data-testid="partner-passport-related-bookings">
              {relatedBookings.map((b) => (
                <li key={b.reference} data-testid={`partner-passport-related-${b.reference}`} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-b-0 last:pb-0">
                  <div className="min-w-0 flex items-baseline gap-3">
                    <code className="text-[11px] font-mono bg-slate-100 px-1.5 py-0.5 rounded">{b.reference}</code>
                    <span className="text-slate-700 truncate">{b.test_id}</span>
                    <span className="text-[11px] text-slate-500">{fmtDate(b.created_at)}</span>
                  </div>
                  <Link to={`/partner/bookings/${b.reference}`} className="text-xs font-semibold text-cyan-700 hover:underline">
                    View booking →
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-6 text-xs text-slate-500" data-testid="partner-passport-detail-disclaimer">
        Internal lab notes, admin-only fields, payment metadata and commission details are not surfaced in the partner view.
      </div>
    </>
  );
}
